"use client";
import { useEffect, useState } from "react";
import type {
  IssueRelations,
  PatchRelations,
} from "@/server/workflow-relations";

/**
 * Fetches the related-entity bundle for an issue. Render-time
 * thin wrapper around the dedicated API; caches per-key in
 * component state so re-renders don't re-fetch.
 *
 * Importing types from `@/server/workflow-relations` is type-only
 * — Next strips the import at runtime, no server modules pulled
 * into the client bundle.
 */
/**
 * `reloadKey` re-fetches when it changes. The issue page passes the mission id,
 * its refresh nonce and a count of the mission's patch-producing events, so a
 * restarted mission, or a patch it writes while the page is open, reaches the
 * page without a reload.
 */
export function useIssueRelations(issueKey: string | null | undefined, reloadKey?: string) {
  const [data, setData] = useState<IssueRelations | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!issueKey) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/issues/${encodeURIComponent(issueKey)}/related`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: IssueRelations) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [issueKey, reloadKey]);

  return { data, loading, error };
}

export function usePatchRelations(patchId: string | null | undefined) {
  const [data, setData] = useState<PatchRelations | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!patchId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/patches/${encodeURIComponent(patchId)}/related`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: PatchRelations) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patchId]);

  return { data, loading, error };
}
